// CSS properties that html2canvas cannot parse when set to modern color() syntax.
// Extend this list if other properties cause similar issues.
const PATCHED_CSS_PROPS = ['background-color'] as const;

const SRGB_RE = /^color\(\s*srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\s*\)$/;

function srgbToRgba(value: string): string | null {
    const m = SRGB_RE.exec(value.trim());
    if (!m) return null;
    const r = Math.min(255, Math.round(parseFloat(m[1]) * 255));
    const g = Math.min(255, Math.round(parseFloat(m[2]) * 255));
    const b = Math.min(255, Math.round(parseFloat(m[3]) * 255));
    const a = m[4] !== undefined ? parseFloat(m[4]) : 1;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * Temporarily replaces unsupported `color(srgb ...)` computed styles within
 * `root` with equivalent `rgba()` inline overrides so html2canvas can render
 * them. Returns a cleanup function that restores the original inline values.
 *
 * Uses TreeWalker for performance: no array allocation, skips non-element
 * nodes natively. The `.includes('color(')` fast-path skips the regex on the
 * vast majority of elements.
 */
export function patchUnsupportedColors(root: HTMLElement): () => void {
    const overrides: Array<{ el: HTMLElement; prop: string; prev: string | null }> = [];

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
    let node: Node | null = walker.currentNode;

    while (node) {
        const el = node as HTMLElement;
        const cs = getComputedStyle(el);

        for (const prop of PATCHED_CSS_PROPS) {
            const val = cs.getPropertyValue(prop);
            if (!val.includes('color(')) continue;
            const rgba = srgbToRgba(val);
            if (rgba) {
                overrides.push({ el, prop, prev: el.style.getPropertyValue(prop) || null });
                el.style.setProperty(prop, rgba);
            }
        }

        node = walker.nextNode();
    }

    return () => {
        for (const { el, prop, prev } of overrides) {
            if (prev !== null) {
                el.style.setProperty(prop, prev);
            } else {
                el.style.removeProperty(prop);
            }
        }
    };
}

export interface DecoratedCaptureResult {
    blob: Blob;
    width: number;
    height: number;
    kb: number;
    bytes: number;
    jpg: Blob;
}

export function cropCanvasToVisible(
    root: HTMLElement,
    fullCanvas: HTMLCanvasElement,
    scale: number,
): HTMLCanvasElement {
    const rect = root.getBoundingClientRect();

    const viewportTop = 0;
    const viewportBottom = window.innerHeight;

    const visibleTopInRoot = Math.max(0, viewportTop - rect.top);
    const visibleBottomInRoot = Math.min(rect.height, viewportBottom - rect.top);
    const visibleHeightCss = Math.max(0, visibleBottomInRoot - visibleTopInRoot);

    if (visibleHeightCss <= 0) {
        return fullCanvas;
    }

    const cssToCanvas = scale;
    const sx = 0;
    const sy = visibleTopInRoot * cssToCanvas;
    const sw = fullCanvas.width; // full width of root
    const sh = visibleHeightCss * cssToCanvas;

    const cropped = document.createElement('canvas');
    cropped.width = sw;
    cropped.height = sh;

    const ctx = cropped.getContext('2d')!;
    ctx.drawImage(fullCanvas, sx, sy, sw, sh, 0, 0, sw, sh);

    return cropped;
}

export async function decorateCapture(
    srcCanvas: HTMLCanvasElement,
    logoSrc: string,
    isMobile: boolean = false,
): Promise<DecoratedCaptureResult> {
    try {
        const LOGO_SCALE_PERCENT = -0.3;
        const LOGO_DEFAULT_W = 280;
        const LOGO_DEFAULT_H = 46;
        const BORDER = 4;
        const LOGO_MAX_W = LOGO_DEFAULT_W + Math.round(LOGO_DEFAULT_W * LOGO_SCALE_PERCENT);
        const LOGO_MAX_H = LOGO_DEFAULT_H + Math.round(LOGO_DEFAULT_H * LOGO_SCALE_PERCENT);
        const LOGO_MARGIN = 10;

        const w = srcCanvas.width;
        const h = srcCanvas.height;

        const outW = w + BORDER * 2;
        const outH = h + BORDER * 2;

        const out = document.createElement('canvas');
        out.width = outW;
        out.height = outH;
        const ctx = out.getContext('2d')!;

        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, outW, outH);
        ctx.drawImage(srcCanvas, BORDER, BORDER);
        // ctx.save();

        const logo = await loadImage(logoSrc);
        const fit = fitInto(logo.naturalWidth * 2, logo.naturalHeight * 2, LOGO_MAX_W, LOGO_MAX_H);
        const lx = Math.round(BORDER + (w - fit.w) / 2);
        const ly = Math.round(BORDER + LOGO_MARGIN);
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
        ctx.shadowBlur = isMobile ? 10 : 18;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = isMobile ? 3 : 6;
        ctx.drawImage(logo, lx, ly, fit.w, fit.h);
        ctx.restore();
        ctx.drawImage(logo, lx, ly, fit.w, fit.h);

        const blob = await new Promise<Blob>((resolve) =>
            out.toBlob((b) => resolve(b as Blob), 'image/png', 1),
        );
        const jpg = await new Promise<Blob>((resolve) =>
            out.toBlob((b) => resolve(b as Blob), 'image/jpeg', 1),
        );
        const bytes = blob.size;
        const kb = +(bytes / 1024).toFixed(2);

        return { blob, width: outW, height: outH, kb, bytes, jpg };
    } catch (error) {
        console.error('Error decorating capture:', error);
        throw error;
    }
}

function fitInto(sw: number, sh: number, maxW: number, maxH: number) {
    const s = Math.min(maxW / sw, maxH / sh, 1);
    return { w: Math.round(sw * s), h: Math.round(sh * s) };
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((res, rej) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = src;
    });
}

/** Creates a temporary browser object URL from a Blob for use in links or image previews. */
export const makeObjectUrl = (blob: Blob): string => {
    return URL.createObjectURL(blob);
};
/** Revokes a previously created object URL to release the underlying Blob from memory. */
export const revokeObjectUrl = (url: string) => {
    URL.revokeObjectURL(url);
};

/** Triggers a browser file download for a Blob as a .jpg file, then revokes the object URL. */
export const downloadBlob = (blob: Blob, filename = `HyperTracker-capture-${Date.now()}`) => {
    const url = makeObjectUrl(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.jpg`;
    a.click();
    revokeObjectUrl(url);
};

/** Copies a Blob to the system clipboard using the Clipboard API (requires user permission). */
export const copyBlobToClipboard = async (blob: Blob) => {
    try {
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
    } catch (error) {
        console.log(error);
    }
};
